"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { useForm } from "react-hook-form";
import Button from "@/components/ui/button";
import CollapsibleText from "@/components/ui/collapsible-text";
import { DatePickerWithYearDropdown } from "@/components/ui/date-picker";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import Input from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useUser } from "@/contexts/user-provider";
import { useClinicClock } from "@/hooks/use-clinic-clock";
import { dayMarker } from "@/lib/clinic-clock";
import { UnitConversions } from "@/types/enums/unit";
import { MeasurementCreate, MeasurementSummary } from "@/types/measurement";
import { useClinicDayField } from "./use-clinic-day-field";
import { useAddMeasurementMutation } from "@/api/provider/user/patient/patient-mutation";
interface AddWeightMeasurementProps {
  patientId: string;
  onSubmit?: (data: MeasurementSummary) => void;
  children: React.ReactNode;
  selectedNoteId?: string;
  selectedEventId?: string;
}
type FormValues = {
  weight: number;
};
export default function AddWeightMeasurement(t0) {
  const $ = _c(55);
  const {
    patientId,
    onSubmit,
    children,
    selectedNoteId,
    selectedEventId
  } = t0;
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Date();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [measurementDate, setMeasurementDate] = useState(t1);
  const addMeasurementMutation = useAddMeasurementMutation();
  const {
    currentUser
  } = useUser();
  const clock = useClinicClock();
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = moment => setMeasurementDate(moment ?? null);
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const measurementDay = useClinicDayField(measurementDate, t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      defaultValues: {
        weight: undefined
      },
      mode: "onSubmit"
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    register,
    handleSubmit,
    reset,
    formState: t4
  } = useForm(t3);
  const {
    errors
  } = t4;
  let t5;
  if ($[3] !== addMeasurementMutation || $[4] !== currentUser?.id || $[5] !== measurementDate || $[6] !== onSubmit || $[7] !== patientId || $[8] !== reset || $[9] !== selectedEventId || $[10] !== selectedNoteId) {
    t5 = async data => {
      if (!currentUser?.id) {
        return;
      }
      setIsSubmitting(true);
      const weightInKg = UnitConversions.lbsToKg(data.weight) ?? 0;
      const formData = {
        patientId,
        weight: weightInKg,
        noteId: selectedNoteId,
        eventId: selectedEventId,
        measurementDate: measurementDate?.toISOString() ?? new Date().toISOString()
      };
      const result = await addMeasurementMutation.mutateAsync(formData);
      if (result) {
        onSubmit?.(result);
        reset();
        setMeasurementDate(new Date());
        setOpen(false);
      }
      setIsSubmitting(false);
    };
    $[3] = addMeasurementMutation;
    $[4] = currentUser?.id;
    $[5] = measurementDate;
    $[6] = onSubmit;
    $[7] = patientId;
    $[8] = reset;
    $[9] = selectedEventId;
    $[10] = selectedNoteId;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleFormSubmit = t5;
  let t6;
  if ($[12] !== reset) {
    t6 = () => {
      reset();
      setMeasurementDate(new Date());
      setOpen(false);
    };
    $[12] = reset;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const closeDialog = t6;
  let t7;
  if ($[14] !== children) {
    t7 = <DialogTrigger asChild={true}>{children}</DialogTrigger>;
    $[14] = children;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <DialogHeader><DialogTitle>Add Weight Measurement</DialogTitle><DialogDescription>The AI will add measurements automatically if they're mentioned in the note, but you can also add them manually here.</DialogDescription></DialogHeader>;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== handleFormSubmit || $[18] !== handleSubmit) {
    t9 = handleSubmit(handleFormSubmit);
    $[17] = handleFormSubmit;
    $[18] = handleSubmit;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Label htmlFor="weight">Weight (lbs)</Label>;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const t11 = !!errors.weight;
  const t12 = !!errors.weight;
  const t13 = errors.weight?.message;
  let t14;
  if ($[21] !== register) {
    t14 = register("weight", {
      required: "Please enter a valid weight",
      min: {
        value: 0,
        message: "Weight must be greater than 0"
      },
      valueAsNumber: true
    });
    $[21] = register;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t11 || $[24] !== t12 || $[25] !== t13 || $[26] !== t14) {
    t15 = <div className="space-y-2">{t10}<Input id="weight" type="number" step="0.01" className="w-full" placeholder="Enter weight in pounds" aria-invalid={t11} hasError={t12} errorMessage={t13} {...t14} /></div>;
    $[23] = t11;
    $[24] = t12;
    $[25] = t13;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t15 || $[29] !== t9) {
    t16 = <form id="add-weight-measurement-form" onSubmit={t9}>{t15}</form>;
    $[28] = t15;
    $[29] = t9;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Label htmlFor="measurement-date">Measurement Date</Label>;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  const t18 = measurementDay.value;
  const t19 = measurementDay.onChange;
  let t20;
  if ($[32] !== clock) {
    t20 = dayMarker(clock.today());
    $[32] = clock;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== measurementDay.onChange || $[35] !== measurementDay.value || $[36] !== t20) {
    t21 = <CollapsibleText text="Set measurement date" className="mt-4"><div className="space-y-2">{t17}<DatePickerWithYearDropdown value={t18} onChange={t19} placeholder="Select measurement date" maxDate={t20} /></div></CollapsibleText>;
    $[34] = measurementDay.onChange;
    $[35] = measurementDay.value;
    $[36] = t20;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] !== t16 || $[39] !== t21) {
    t22 = <DialogBody>{t16}{t21}</DialogBody>;
    $[38] = t16;
    $[39] = t21;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== closeDialog) {
    t23 = <Button variant="secondary" type="button" onClick={closeDialog}>Cancel</Button>;
    $[41] = closeDialog;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  if ($[43] !== isSubmitting) {
    t24 = <Button variant="primary" type="submit" disabled={isSubmitting} isLoading={isSubmitting} loadingText="Saving..." form="add-weight-measurement-form">Save Measurement</Button>;
    $[43] = isSubmitting;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== t23 || $[46] !== t24) {
    t25 = <DialogFooter>{t23}{t24}</DialogFooter>;
    $[45] = t23;
    $[46] = t24;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  let t26;
  if ($[48] !== t22 || $[49] !== t25) {
    t26 = <DialogContent>{t8}{t22}{t25}</DialogContent>;
    $[48] = t22;
    $[49] = t25;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  let t27;
  if ($[51] !== open || $[52] !== t26 || $[53] !== t7) {
    t27 = <Dialog open={open} onOpenChange={setOpen}>{t7}{t26}</Dialog>;
    $[51] = open;
    $[52] = t26;
    $[53] = t7;
    $[54] = t27;
  } else {
    t27 = $[54];
  }
  return t27;
}